import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser';
import { useTranslation } from 'react-i18next';
import { User } from 'models/User';
import FieldPhoneNumber from 'components/FieldPhoneNumber';
import InputFieldComponent from 'components/InputField';
import { Button } from 'ncoded-component-library';

import './PhoneVerifForm.styles.scss';
import './PhoneVerifForm.styles.responsive.scss';

type PhoneVerifFormProps = {
  className?: string;
};

type PhoneNumberForm = Pick<User, 'phoneNumber'> & {
  verificationCode?: string;
};

const PhoneVerifForm: React.FC<PhoneVerifFormProps> = (props) => {
  const { className } = props;
  const { popError, popSuccess } = useContext(PopNotificationsContext);
  const { user, updateUser } = useContext(CurrentUserContext);
  const { t } = useTranslation();

  const classes = classNames('phone-verif-form', className);

  const handleChangePhone = useCallback(
    async (values: PhoneNumberForm) => {
      const { phoneNumber } = values;

      try {
        const {
          data: { user },
        } = await api.user.changePhoneNumber(phoneNumber);
        updateUser(user);
        popSuccess(t('verification-send-success'));
      } catch (error) {
        console.error(error);
        popError(t('verification-send-error'));
      }
    },
    [popError, popSuccess, t, updateUser],
  );

  const handleSendAgain = useCallback(async () => {
    try {
      await api.twilio.initiateVerification();
      popSuccess(t('verification-send-success'));
    } catch (error: any) {
      popError(t('verification-send-error'));
      console.error(error);
    }
  }, [popError, popSuccess, t]);

  const handleVerify = useCallback(
    async (verificationCode: string) => {
      try {
        const { data } = await api.twilio.verificateCode(verificationCode);
        updateUser(data);
        popSuccess(t('verify-phone-success'));
      } catch (error: any) {
        popError(t('verify-phone-error'));
        console.error(error);
      }
    },
    [popError, popSuccess, t, updateUser],
  );

  return (
    <div className={classes}>
      <Form<PhoneNumberForm>
        onSubmit={handleChangePhone}
        initialValues={{ phoneNumber: user.phoneNumber }}
        render={({ handleSubmit, submitting, valid, dirty, values }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="personal-info__form"
              autoComplete="off"
            >
              <FieldPhoneNumber
                name="phoneNumber"
                placeholder={t('phone-number-placeholder')}
                label={t('phone-number')}
                required
              />

              {!user.isPhoneNumberConfirmed && (
                <>
                  <p>
                    {t('verify-phone-paragraph')}
                    <span>{`${user.phoneNumber?.refferenceNumber} ${user.phoneNumber?.number}`}</span>
                  </p>
                  <Field
                    name="verificationCode"
                    component={InputFieldComponent}
                    placeholder={t('verification-code')}
                  />
                  <Button
                    onClick={() => handleVerify(values.verificationCode)}
                    disabled={submitting || !valid}
                  >
                    {t('verify')}
                  </Button>
                  <p>
                    {t('code-not-received')}
                    <Button variant="link" onClick={handleSendAgain}>
                      {t('send-again')}
                    </Button>
                  </p>
                </>
              )}

              {dirty && (
                <Button type="submit" disabled={submitting || !valid}>
                  {t('change-phone-number')}
                </Button>
              )}
            </form>
          );
        }}
      />
    </div>
  );
};

export default PhoneVerifForm;
